import React from "react";
import ModalContentBaseXl from '../redux/components/modal/modal_content_base_xl';
import ModalAlert from '../redux/components/modal/modal_alert';
import ModalLoader from '../redux/components/modal/modal_loader';
import axios from '../axios';
import usePresignedImageUpload from '../../lib/use_presigned_image_upload';
import "babel-polyfill";

export const GenerateOutlineFromPhotoModal = ({
  eventId,
  mainImageUrl,
  onAcceptEventTitle,
  onAcceptEventBody,
  onAcceptEventDate,
  onAcceptEventEndDatetime,
  onAcceptPlace,
  onMainImageConfirmed,
  onSubImageConfirmed,
  subImageCount,
}) => {
  const modalRef = React.useRef();
  const errorModalRef = React.useRef();
  const rateLimitModalRef = React.useRef();
  const loaderRef = React.useRef();
  const reflectLoaderRef = React.useRef();
  const photoInputRef = React.useRef();
  const { upload } = usePresignedImageUpload();

  const [photoCacheData, setPhotoCacheData] = React.useState('');
  const [photoPreviewUrl, setPhotoPreviewUrl] = React.useState('');
  const [eventTitle, setEventTitle] = React.useState('');
  const [eventBody, setEventBody] = React.useState('');
  const [eventDate, setEventDate] = React.useState('');
  const [eventEndDatetime, setEventEndDatetime] = React.useState('');
  const [place, setPlace] = React.useState('');
  const [isImageRegistered, setIsImageRegistered] = React.useState(true);

  // イベント画像はメイン1枚＋サブ4枚が上限
  const isSubImageFull = !!mainImageUrl && subImageCount >= 4;

  // createObjectURL で作ったURLは明示的に解放しないとブラウザが画像を保持し続ける
  const revokePhotoPreviewUrl = () => {
    photoPreviewUrl && URL.revokeObjectURL(photoPreviewUrl);
  };

  const close = () => {
    revokePhotoPreviewUrl();
    setPhotoCacheData('');
    setPhotoPreviewUrl('');
    setEventTitle('');
    setEventBody('');
    setEventDate('');
    setEventEndDatetime('');
    setPlace('');
    setIsImageRegistered(true);
    modalRef.current && modalRef.current.closeModal();
  };

  const generateFromPhoto = async () => {
    const photoFile = photoInputRef.current.files[0];
    if (!photoFile) {
      return;
    }

    try {
      loaderRef.current.openModal();

      const uploadedCacheData = await upload(photoFile);
      setPhotoCacheData(uploadedCacheData);
      revokePhotoPreviewUrl();
      setPhotoPreviewUrl(URL.createObjectURL(photoFile));

      const response = await fetch('/api/event/generate_outline_from_image', {
        method: 'post',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ cache_data: uploadedCacheData, event_id: eventId }),
      });
      if (!response.ok) {
        loaderRef.current.closeModal();
        if (response.status === 429) {
          rateLimitModalRef.current.openModal();
        } else {
          errorModalRef.current.openModal();
        }
        return;
      }

      const json = await response.json();
      setEventTitle(json.result.title || '');
      setEventBody(json.result.body || '');
      setEventDate(json.result.event_date || '');
      setEventEndDatetime(json.result.event_end_datetime || '');
      setPlace(json.result.place || '');

      loaderRef.current.closeModal();
    } catch (err) {
      loaderRef.current.closeModal();
      errorModalRef.current.openModal();
    } finally {
      photoInputRef.current.value = '';
    }
  };

  const reflectToEventPage = async () => {
    if (eventTitle) {
      onAcceptEventTitle && onAcceptEventTitle(eventTitle);
    }
    if (eventBody) {
      onAcceptEventBody && onAcceptEventBody(eventBody);
    }
    if (eventDate) {
      onAcceptEventDate && onAcceptEventDate(eventDate);
    }
    if (eventEndDatetime) {
      onAcceptEventEndDatetime && onAcceptEventEndDatetime(eventEndDatetime);
    }
    if (place) {
      onAcceptPlace && onAcceptPlace(place);
    }

    // メイン画像が空なら1枚目に、埋まっていれば既存を上書きせず末尾に足す
    if (isImageRegistered && photoCacheData && !isSubImageFull) {
      try {
        reflectLoaderRef.current.openModal();
        if (mainImageUrl) {
          const res = await axios.post('/api/event_edit/confirm_sub_image_upload', {
            event_id: eventId,
            cache_data: photoCacheData,
          });
          onSubImageConfirmed && onSubImageConfirmed(res.data);
        } else {
          const res = await axios.post('/api/event/confirm_main_image_upload', {
            event_id: eventId,
            cache_data: photoCacheData,
          });
          onMainImageConfirmed && onMainImageConfirmed(res.data.main_image_url);
        }
        reflectLoaderRef.current.closeModal();
      } catch (error) {
        reflectLoaderRef.current.closeModal();
        alert(
          error?.response?.data?.error || 'イベント画像の登録に失敗しました。'
        );
        return;
      }
    }

    alert('イベントに反映しました。');
    close();
  };

  const previewContent = (
    <button
      type="button"
      className="a-button a-button--sm a-button--orange"
      style={{borderRadius: 4}}
    >
      作成する
    </button>
  );

  const modalContent = (
    <div>
      <div
        className="bg-white w-100 border-bottom"
        style={{
          zIndex: 9998,
          borderRadius: '16px 16px 0 0',
          position: 'fixed',
        }}
      >
        <button
          type="button"
          className="close close__modal position--modalclose"
          aria-label="Close"
          onClick={() => close()}
        >
          <span className="text-fz-24px float-right m-2" aria-hidden="true">
            ×
          </span>
        </button>
      </div>
      <div
        className="card bg-gray"
        style={{ paddingTop: '38px' }}
      >
        <div className="shadow-sm mb-3 bg-white">
          <div className="container col-md-8 col-lg-6">
            <div className="row justify-content-between p-3_5">
              <label htmlFor="generate-outline-photo">イベント内容が写ったチラシやメモの写真を選ぼう</label>
              <input
                type="file"
                accept="image/*"
                id="generate-outline-photo"
                ref={photoInputRef}
                className="d-none"
                onChange={async () => await generateFromPhoto()}
              />
              {photoPreviewUrl &&
                <img
                  src={photoPreviewUrl}
                  alt="選択した写真"
                  className="img-fluid mb-2"
                  style={{ maxHeight: 200, objectFit: 'contain' }}
                />
              }
              <button
                type="button"
                className="a-button a-button__top a-button--orange my-4 w-60"
                onClick={() => photoInputRef.current.click()}
              >写真を撮影・選択する</button>
            </div>
            {(eventTitle || eventBody) &&
              <React.Fragment>
                <div className="row justify-content-between p-3_5">
                  <p className="w-100 mb-2">この写真をイベント画像に登録する</p>
                  {isSubImageFull
                    ? <p className="w-100 text-gray text-fz-12px mb-0">イベント画像が上限の5枚に達しているため登録できません。</p>
                    : <div className="w-100 d-flex">
                        <label className="mr-4 mb-0">
                          <input
                            type="radio"
                            name="generated-outline-photo-image-registered"
                            className="mr-1"
                            checked={isImageRegistered}
                            onChange={() => setIsImageRegistered(true)}
                          />
                          登録する
                        </label>
                        <label className="mb-0">
                          <input
                            type="radio"
                            name="generated-outline-photo-image-registered"
                            className="mr-1"
                            checked={!isImageRegistered}
                            onChange={() => setIsImageRegistered(false)}
                          />
                          登録しない
                        </label>
                      </div>
                  }
                </div>
                <button
                  type="button"
                  className="a-button a-button__top a-button--orange w-60"
                  style={{ borderRadius: 4 }}
                  onClick={async () => await reflectToEventPage()}
                >イベントページに反映</button>
              </React.Fragment>
            }
            <div className="row justify-content-between p-3_5">
              <div className="w-100 d-flex justify-content-between align-items-center mb-2">
                <label htmlFor="generated-outline-photo-title">イベント名</label>
              </div>
              <textarea
                id="generated-outline-photo-title"
                className="form-control"
                style={{minHeight: 'calc(1.5em + 0.75rem + 2px)', height: 'auto'}}
                value={eventTitle}
                onChange={(e) => {
                  setEventTitle(e.target.value)
                }}
              />
            </div>
            <div className="row justify-content-between p-3_5">
              <div className="w-100 d-flex justify-content-between align-items-center mb-2">
                <label htmlFor="generated-outline-photo-body">イベントの説明</label>
              </div>
              <textarea
                id="generated-outline-photo-body"
                className="form-control"
                style={{height: 200, overflow: 'auto', whiteSpace: 'pre-wrap'}}
                value={eventBody}
                onChange={(e) => {
                  setEventBody(e.target.value)
                }}
              />
            </div>
            <div className="row justify-content-between p-3_5">
              <div className="w-100 d-flex justify-content-between align-items-center mb-2">
                <label htmlFor="generated-outline-photo-event-date">開始日時</label>
              </div>
              <input
                type="datetime-local"
                id="generated-outline-photo-event-date"
                className="form-control"
                value={eventDate}
                onChange={(e) => {
                  setEventDate(e.target.value)
                }}
              />
            </div>
            <div className="row justify-content-between p-3_5">
              <div className="w-100 d-flex justify-content-between align-items-center mb-2">
                <label htmlFor="generated-outline-photo-event-end-datetime">終了日時</label>
              </div>
              <input
                type="datetime-local"
                id="generated-outline-photo-event-end-datetime"
                className="form-control"
                value={eventEndDatetime}
                onChange={(e) => {
                  setEventEndDatetime(e.target.value)
                }}
              />
            </div>
            <div className="row justify-content-between p-3_5">
              <div className="w-100 d-flex justify-content-between align-items-center mb-2">
                <label htmlFor="generated-outline-photo-place">開催場所</label>
              </div>
              <input
                type="text"
                id="generated-outline-photo-place"
                className="form-control"
                value={place}
                placeholder="新宿駅西口"
                onChange={(e) => {
                  setPlace(e.target.value)
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <React.Fragment>
      <ModalContentBaseXl ref={modalRef} previewContent={previewContent} modalContent={modalContent} />
      <ModalAlert
        ref={errorModalRef}
        id="modal-generated-from-photo-error"
        title="自動生成失敗"
        content="自動生成に失敗しました。時間をおいてもう一度お試しください。"
      />
      <ModalAlert
        ref={rateLimitModalRef}
        id="modal-rate-limit-from-photo-error"
        title="自動生成失敗"
        content="1日の利用数を超過しました。24時間後にもう一度お試しください。"
      />
      <ModalLoader ref={loaderRef} message="約1分お待ちください" dummyProgressShown />
      <ModalLoader ref={reflectLoaderRef} />
    </React.Fragment>
  );
};
